import { MouseEvent } from "react";
import { Stack } from "@mui/material";
import { useSignal } from "@preact/signals-react";
import { isNil, isString, isArray } from "lodash-es";
import NGSync from "../components/NGSync/NGSync";
import useResizable from "../hooks/useResizable";
import NGLayout from "../layouts/NGLayout";
import { setupForm, setupHandlers, setupLocalState, updateItemContext } from "../library/dataService";
import { INGSimpleContainerProps } from "../library/NGFieldExtensions";
import { generateUID, getClassName, getFirstNonEmpty, getTestId } from "../library/utils";
import { containerWithPaper } from "./GeneratorUtils";
import NGLayoutItem from "./NGLayoutItem";
import { publishUserAction } from "../library/analytics";

export default function NGSimpleContainer({ config, context, children }: INGSimpleContainerProps) {
  const isForm = config.CreateForm || config.Data || config.Bindings?.Data;
  const currentContext = updateItemContext(context, config, { IsForm: isForm });

  const local = setupLocalState(
    config,
    {
      Sync: useSignal(config.Sync ?? {}),
      Data: useSignal(config.Data ?? {}),
      Visible: useSignal(config.Visible ?? true),
      Resizable: useSignal(config.Resizable ?? false),
      InDesignMode: useSignal(config.InDesignMode ?? false),
      InReviewMode: useSignal(config.InReviewMode ?? false),
      Style: useSignal(config.Style ?? {}),
      Classes: useSignal(config.Classes ?? []),
    },
    currentContext
  );

  // AA-TODO: Setup form if container is a form
  if (isForm) {
    currentContext.Form = { Data: local.Data, Config: config };
    setupForm(currentContext);
  }

  if (!config.IgnoreLayout && !config.Layout) {
    config.Layout = {
      __typename: "FlexLayout",
      Id: generateUID(),
    };
  }

  if (!isNil(config.ReviewDialogOptions)) {
    (config.ReviewDialogOptions as any).ContextId = (config as any).ContextId;
  }

  const handlers = setupHandlers(config, currentContext);

  const { resizable } = useResizable(local.Resizable.value, null, `resizable-simple-container-${config.Id}`);

  function renderContainer(addTestId: boolean) {
    return (
      <>
        {config.IgnoreLayout &&
          resizable(
            <Stack
              data-testid={addTestId ? getTestId(config) : undefined}
              data-type={addTestId ? config.__typename : undefined}
              sx={local.Style.value}
              className={getClassName(local.Classes)}
              {...handlers}
              onClick={(e: MouseEvent<HTMLDivElement>) => {
                if (handlers["onClick"]) {
                  const eventName = context?.Repeater ? "repeater_row_click" : "container_click";
                  const data = context?.Row || config;
                  const action = publishUserAction(config, context, eventName, e, {
                    ElementText: getFirstNonEmpty(
                      config.UniqueName,
                    ),
                    EventCategory: "engagement",
                    ElementClass: isString(local.Classes.value) ? local.Classes.value : isArray(local.Classes.value) ? local.Classes.value.join(" ") : "",
                  });
                  handlers["onClick"](e, data, action);
                }
              }}
            >
              {config.Items?.map((item) => (
                <NGLayoutItem key={(item as any).Id} config={item} context={currentContext} />
              ))}
              {children}
            </Stack>
          )}
        {!config.IgnoreLayout &&
          config.Layout &&
          resizable(
            <NGLayout
              layout={{ ...config.Layout, Classes: config.Classes }}
              layoutItem={config}
              inDesignMode={local.InDesignMode.value}
              context={currentContext}
            />
          )}
        {!config.IgnoreLayout && local.InReviewMode.value && resizable(<></>)}
        {config.Sync && <NGSync config={{ ...config.Sync, ContainerId: config.Id }} context={currentContext} />}
      </>
    );
  }

  return containerWithPaper(local, config, renderContainer, context);
}
